import { useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Collapse, Drawer, Space, Spin } from 'antd';
import type { AreaAggregationType } from '../../../infrastructure/dataApi/client';
import { useAggregationConfig } from '../../../hooks/SimViewer/useAggregationConfig';
import {
  useDataFilter,
  type LocationFilterType,
} from '../../../hooks/SimViewer/useDataFilter';
import { useMetaData } from '../../../hooks/data/useMetaData';
import { useAreasData } from '../../../hooks/data/useAreasData';
import { useAreaAggregationData } from '../../../hooks/data/useAreaAggregationData';
import { downloadAsCsv } from '../../../utils/file';
import { toPascal } from '../../../utils/case';
import { BarChart, type BarItem } from '../Chart/BarChart';
import { MapMarkedIconButton } from '../../MapMarkedIconButton';
import { ActionButtons } from './ActionButtons';
import { AreaAggregationComparison } from './AreaAggregationComparison';

const FILTER_KEYS = {
  home: 'home',
  work: 'work',
  trip_start: 'start',
  trip_end: 'end',
} as const satisfies Record<
  'home' | 'work' | 'trip_start' | 'trip_end',
  LocationFilterType
>;

const AGGREGATION_TYPES = {
  home: 'home_agents_per_unit_area',
  work: 'work_agents_per_unit_area',
  trip_start: 'start_trips_per_unit_area',
  trip_end: 'end_trips_per_unit_area',
} as const satisfies Record<
  'home' | 'work' | 'trip_start' | 'trip_end',
  AreaAggregationType
>;

const SQUARE_KILOMETER = 1000 * 1000;

type Props = {
  type: 'home' | 'work' | 'trip_start' | 'trip_end';
  icon: ReactNode;
  label: string;
  extra?: ReactNode;
};

const AreaRankingPanel = ({
  type,
  icon,
  label,
  extra,
  ...props
}: Props): ReactNode => {
  const { setStopBy } = useDataFilter();
  const { aggregationConfig, startAggregation, clearAggregation } =
    useAggregationConfig();

  return (
    <Collapse.Panel
      key={type}
      header={
        <Space>
          {icon}
          {label}
          {extra}
        </Space>
      }
      extra={
        <MapMarkedIconButton
          active={aggregationConfig?.target === type}
          onClick={(): void => {
            if (aggregationConfig?.target === type) {
              clearAggregation();
            } else {
              startAggregation(type);
            }
            setStopBy(null);
          }}
        />
      }
      {...props}
    >
      <AreaRankingPanelContent type={type} label={label} />
    </Collapse.Panel>
  );
};

const AreaRankingPanelContent = ({
  type,
  label,
}: Pick<Props, 'type' | 'label'>): ReactNode => {
  const { t } = useTranslation();
  const { currentDataMeta } = useMetaData();
  const { dataFilter, updateAreaFilter } = useDataFilter();
  const { findArea } = useAreasData();
  const { aggregationConfig } = useAggregationConfig();

  // 居住地は都道府県単位での可視化中のみ、都道府県単位の集計に切り替える
  const aggregationType =
    type === 'home' && aggregationConfig?.type === 'area_pref'
      ? 'home_pref_agents_per_unit_area'
      : AGGREGATION_TYPES[type];
  const { isLoading, aggregations } = useAreaAggregationData(aggregationType);
  const viewMagnification = 1 / Number(currentDataMeta?.reduction_factor ?? 1);

  const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);

  const ranking = useMemo(() => {
    return Array.from(aggregations)
      .sort(
        (
          [, value1]: [string, number],
          [, value2]: [string, number]
        ): number => {
          // 集計値の大きい順にソートする
          return -(value1 - value2);
        }
      )
      .slice(0, 10)
      .map(([keyCode, value]: [string, number]): BarItem => {
        return {
          key: keyCode,
          value: value * viewMagnification * SQUARE_KILOMETER,
          label: findArea(keyCode)?.s_name ?? `(${t('unknown')})`,
        };
      });
  }, [aggregations, viewMagnification, findArea, t]);

  return (
    <Spin spinning={isLoading}>
      <BarChart
        disablePercentage
        data={ranking}
        onBarItemClick={(key: string): void => {
          updateAreaFilter([key], {
            [FILTER_KEYS[type]]:
              !dataFilter[FILTER_KEYS[type]].areaKeyCodes.includes(key),
          });
        }}
        noDataMessage={t('message.noTripFound')}
        unit={t('unit.agentPerArea')}
      />
      <ActionButtons
        onOpenComparison={(): void => {
          setIsDrawerOpen(true);
        }}
        onExportCsv={(withBom: boolean): void => {
          downloadAsCsv(
            [
              [label, t('agentCount')],
              ...ranking.map((row): (string | number)[] => {
                return [row.label, row.value];
              }),
            ],
            `${currentDataMeta?.name ?? ''}_${toPascal(label)}.csv`,
            { withBom: withBom }
          );
        }}
        disabled={isLoading}
      />
      <Drawer
        title={t('barChartTitle', { target: label })}
        placement="right"
        width="80%"
        destroyOnClose
        open={isDrawerOpen}
        onClose={(): void => {
          setIsDrawerOpen(false);
        }}
      >
        <AreaAggregationComparison
          type={aggregationType}
          noDataMessage={t('message.noAgentFound')}
          unit={t('unit.agentPerArea')}
        />
      </Drawer>
    </Spin>
  );
};

export { AreaRankingPanel };
